/**
 * AIXML Compact Format — agent-optimized shorthand that expands to full DiagramSpec.
 *
 * Key savings vs full spec:
 * - groups[].chain auto-wires sequential edges + inherits layer/parent
 * - edges as [source, target, opts?] — IDs auto-generated
 * - pos: [x,y] shorthand for position
 * - desc shorthand for description
 * - No edge IDs needed
 * - No layer/parentNode on children
 */

import type { DiagramSpec, DiagramNode, DiagramEdge, DiagramView, DiagramLayer, DiagramPreferences } from "./types"

// ── Compact Types (what agents generate) ──

export interface CompactNode {
  id: string
  label: string
  layer: string
  icon?: string
  desc?: string
  tooltip?: string
  type?: string
  fields?: Record<string, string>
  pos?: [number, number]
  rank?: number
  html?: string
  width?: number
  height?: number
}

export interface CompactGroupChild {
  id: string
  label: string
  icon?: string
  desc?: string
  tooltip?: string
  type?: string
  fields?: Record<string, string>
  pos?: [number, number] // relative to group, auto-calculated if omitted
}

export interface CompactGroup {
  id: string
  label: string
  icon?: string
  tooltip?: string
  pos?: [number, number]
  size?: [number, number] // [width, height]
  chain: CompactGroupChild[] // sequential — edges auto-generated between them
  layer?: string // layer for the group node itself (default: first layer)
  childLayer?: string // layer for children (default: second layer or 'step')
}

export type CompactEdge =
  | [string, string] // [source, target]
  | [string, string, CompactEdgeOpts] // [source, target, opts]

export interface CompactEdgeOpts {
  label?: string
  style?: "default" | "dashed" | "straight" | "bezier" | "smoothstep"
}

export interface CompactSpec {
  id: string
  name: string
  desc?: string
  layout?: "TB" | "LR" | "manual" | "radial" // default view layout (default: TB)
  preferences?: DiagramPreferences
  layers: DiagramLayer[]
  nodes?: CompactNode[]
  groups?: CompactGroup[]
  edges?: CompactEdge[]
  views?: DiagramView[] // optional — auto-generated if omitted
}

// ── Expand Function ──

const CHILD_SPACING_Y = 100
const CHILD_OFFSET_X = 20
const CHILD_START_Y = 50

export function expandSpec(compact: CompactSpec): DiagramSpec {
  const nodes: DiagramNode[] = []
  const edges: DiagramEdge[] = []
  let edgeCounter = 0

  // Determine default layers for groups/children
  const groupLayerId = compact.layers.length > 1 ? compact.layers[1].id : compact.layers[0].id
  const childLayerId =
    compact.layers.length > 2 ? compact.layers[2].id : compact.layers[compact.layers.length > 1 ? 1 : 0].id

  // Expand standalone nodes
  if (compact.nodes) {
    for (const n of compact.nodes) {
      nodes.push({
        id: n.id,
        label: n.label,
        layer: n.layer,
        description: n.desc,
        tooltip: n.tooltip,
        icon: n.icon,
        type: n.type,
        fields: n.fields,
        rank: n.rank,
        html: n.html,
        width: n.width,
        height: n.height,
        position: n.pos ? { x: n.pos[0], y: n.pos[1] } : undefined,
      })
    }
  }

  // Expand groups → group nodes + children + chain edges
  if (compact.groups) {
    for (const g of compact.groups) {
      const gLayer = g.layer ?? groupLayerId
      const cLayer = g.childLayer ?? childLayerId

      // Group container node
      nodes.push({
        id: g.id,
        label: g.label,
        icon: g.icon,
        tooltip: g.tooltip,
        layer: gLayer,
        isGroup: true,
        width: g.size?.[0],
        height: g.size?.[1],
        position: g.pos ? { x: g.pos[0], y: g.pos[1] } : undefined,
      })

      // Children
      for (let i = 0; i < g.chain.length; i++) {
        const child = g.chain[i]
        const childPos = child.pos
          ? { x: child.pos[0], y: child.pos[1] }
          : { x: CHILD_OFFSET_X, y: CHILD_START_Y + i * CHILD_SPACING_Y }

        nodes.push({
          id: child.id,
          label: child.label,
          layer: cLayer,
          parentNode: g.id,
          description: child.desc,
          tooltip: child.tooltip,
          icon: child.icon,
          type: child.type,
          fields: child.fields,
          position: childPos,
        })

        // Auto-chain: wire sequential edges between children
        if (i > 0) {
          const prev = g.chain[i - 1]
          edges.push({
            id: `_e${edgeCounter++}`,
            source: prev.id,
            target: child.id,
          })
        }
      }
    }
  }

  // Expand edges
  if (compact.edges) {
    for (const e of compact.edges) {
      const [source, target] = e
      const opts: CompactEdgeOpts = e.length === 3 ? e[2] : {}
      edges.push({
        id: `_e${edgeCounter++}`,
        source,
        target,
        label: typeof opts === "string" ? opts : opts?.label,
        style: typeof opts === "string" ? undefined : opts?.style,
      })
    }
  }

  // Auto-generate views if not provided
  const layout = compact.layout ?? "TB"
  const views: DiagramView[] = compact.views ?? [{ id: "main", name: "Main", layout }]

  return {
    id: compact.id,
    name: compact.name,
    description: compact.desc,
    preferences: compact.preferences,
    layers: compact.layers,
    nodes,
    edges,
    views,
    defaultView: views[0].id,
  }
}

/** Type guard: detect compact vs full spec */
export function isCompactSpec(spec: any): spec is CompactSpec {
  return spec && (Array.isArray(spec.groups) || (spec.edges && Array.isArray(spec.edges[0])))
}
